/**
 * Allowed Origins Configuration
 * 
 * Single source of truth for all allowed origins across:
 * - CSP frame-ancestors in _headers files
 * - Runtime postMessage origin validation
 * 
 * When you add a new admin panel domain, add it here and run `npm run build`.
 * All templates will be updated automatically.
 */

export const allowedOrigins = {
  /**
   * Admin panel origins - production and staging environments
   */
  adminPanel: [
    'https://admin-panel-mu-sand.vercel.app',
  ],

  /**
   * Localhost origins for local development
   * Supports both http and https, with or without ports
   */
  localhost: [
    'http://localhost:3000',
    'http://localhost:3001',
    'http://127.0.0.1:3000',
    'http://127.0.0.1:3001',
    'https://localhost:3000',
    'https://localhost:3001',
  ],

  /**
   * Cloudflare Pages preview deployments
   * Pattern matching for *.pages.dev domains
   */
  cloudflarePatterns: [
    '*.pages.dev',
  ],
};

/**
 * Generate CSP frame-ancestors directive for _headers file
 * @returns {string} CSP directive value
 */
export function generateFrameAncestors() {
  const origins = [
    "'self'",
    ...allowedOrigins.localhost,
    ...allowedOrigins.adminPanel,
  ];
  return origins.join(' ');
}

/**
 * Check if an origin is allowed
 * Used by runtime message-handler.ts for postMessage validation
 * 
 * @param {string} origin - Origin to check (e.g., 'https://example.com')
 * @returns {boolean} True if origin is allowed
 */
export function isOriginAllowed(origin) {
  // Same origin is always allowed
  if (typeof window !== 'undefined' && origin === window.location.origin) {
    return true;
  }

  // Check exact matches (localhost, admin panel)
  const exactOrigins = [
    ...allowedOrigins.localhost,
    ...allowedOrigins.adminPanel,
  ];
  if (exactOrigins.includes(origin)) {
    return true;
  }

  // Check pattern matches (Cloudflare Pages)
  for (const pattern of allowedOrigins.cloudflarePatterns) {
    if (pattern === '*.pages.dev' && origin.endsWith('.pages.dev')) {
      return true;
    }
  }

  return false;
}
